import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Select — shadcn idiom on `@radix-ui/react-select`, themed onto the HofSync
 * brand tokens. This is the FIRST Radix **portal** primitive in the DS, so it
 * exercises a concern the inline primitives (Input/Switch/Button) never hit:
 * the dropdown (`SelectContent`) renders through `SelectPrimitive.Portal` to
 * `document.body`, OUTSIDE the DS subtree. The global compiled stylesheet
 * `dist/styles.css` still styles it, but ONLY because every utility on the
 * portal content is `tw:`-prefixed — drop the prefix and the dropdown renders
 * unstyled in the app. (See CLAUDE.md "Scoping".)
 *
 * Preflight is excluded from the DS Tailwind build, so the trigger — a real
 * `<button>` via Radix — keeps its UA chrome unless reset per-component: we set
 * `tw:appearance-none` + an explicit border/background/focus-ring (exactly like
 * `input.tsx`) + `tw:font-sans` (form controls don't inherit font-family; the UA
 * button font would leak otherwise, #1927). `@radix-ui/react-select` is a real
 * `dependency` (the DS ships as source); the name `Select` is collision-free in
 * the public-API barrel.
 */
const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

export type SelectProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Root
>;
export interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  /**
   * `full` (default) fills its container, as a form field does; `fit` sizes to
   * the value, for a select sitting inline at the end of a row (a member's role).
   */
  width?: 'full' | 'fit';
  /** `sm` matches the other small controls of a row (`Button size="sm"`). */
  controlSize?: 'sm' | 'default';
}
export type SelectContentProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Content
>;
export interface SelectItemProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> {
  /**
   * One line on what choosing this option means (a role's rights). Listed under
   * the option's name and wired as its accessible description; the trigger
   * shows the name only — Radix renders just `ItemText` there.
   */
  description?: React.ReactNode;
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, children, width = 'full', controlSize = 'default', ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'tw:flex tw:appearance-none tw:items-center tw:justify-between tw:gap-2 tw:rounded-md tw:border tw:border-input tw:bg-card tw:px-3 tw:py-2 tw:font-sans tw:text-sm tw:text-foreground tw:shadow-sm tw:transition-colors',
      'tw:data-[placeholder]:text-muted-foreground',
      'tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
      'tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
      width === 'fit' ? 'tw:w-auto tw:min-w-40' : 'tw:w-full',
      controlSize === 'sm' ? 'tw:h-8' : 'tw:h-9',
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="tw:h-4 tw:w-4 tw:opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'tw:flex tw:cursor-default tw:items-center tw:justify-center tw:py-1',
      className
    )}
    {...props}
  >
    <ChevronUp className="tw:h-4 tw:w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'tw:flex tw:cursor-default tw:items-center tw:justify-center tw:py-1',
      className
    )}
    {...props}
  >
    <ChevronDown className="tw:h-4 tw:w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      // Portal content: every utility MUST be tw:-prefixed (it mounts on
      // document.body, styled only by the global dist/styles.css).
      className={cn(
        'tw:relative tw:z-50 tw:max-h-96 tw:min-w-[8rem] tw:overflow-hidden tw:rounded-md tw:border tw:border-border tw:bg-popover tw:text-popover-foreground tw:shadow-md',
        position === 'popper' &&
          'tw:data-[side=bottom]:translate-y-1 tw:data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'tw:p-1',
          position === 'popper' &&
            'tw:h-[var(--radix-select-trigger-height)] tw:w-full tw:min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'tw:px-2 tw:py-1.5 tw:text-xs tw:font-semibold tw:text-muted-foreground',
      className
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(({ className, children, description, ...props }, ref) => {
  const descriptionId = React.useId();
  return (
    <SelectPrimitive.Item
      ref={ref}
      aria-describedby={description ? descriptionId : undefined}
      className={cn(
        'tw:relative tw:flex tw:w-full tw:cursor-default tw:select-none tw:rounded-sm tw:py-1.5 tw:pl-8 tw:pr-2 tw:text-sm tw:text-popover-foreground tw:outline-none',
        description ? 'tw:flex-col tw:items-start tw:gap-0.5 tw:py-2' : 'tw:items-center',
        'tw:focus:bg-accent tw:focus:text-accent-foreground',
        'tw:data-[disabled]:pointer-events-none tw:data-[disabled]:opacity-50',
        className
      )}
      {...props}
    >
      <span
        className={cn(
          'tw:absolute tw:left-2 tw:flex tw:h-4 tw:w-4 tw:items-center tw:justify-center',
          description && 'tw:top-2.5'
        )}
      >
        <SelectPrimitive.ItemIndicator>
          <Check className="tw:h-4 tw:w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <span className={cn(description && 'tw:font-medium')}>
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </span>
      {description && (
        <span
          id={descriptionId}
          className="tw:block tw:max-w-72 tw:whitespace-normal tw:text-xs tw:leading-snug tw:text-muted-foreground"
        >
          {description}
        </span>
      )}
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('tw:-mx-1 tw:my-1 tw:h-px tw:bg-border', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
